export interface OfflineGainData {
  timeAwayMs: number;
  cappedMs: number;
  maxHours: number;
  goldGained: number;
  wave: number;
  goldTargetPos: { x: number; y: number }; // screen pixels, Phaser gold icon position
}

function formatGold(n: number): string {
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(Math.floor(n));
}

function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
}

function drawGameCoin(canvas: HTMLCanvasElement, size: number): void {
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const c = size / 2;
  ctx.beginPath();
  ctx.arc(c, c, c * 0.75, 0, Math.PI * 2);
  ctx.fillStyle = '#FFD700';
  ctx.fill();
  ctx.beginPath();
  ctx.arc(c, c, c * 0.43, 0, Math.PI * 2);
  ctx.fillStyle = '#DAA520';
  ctx.fill();
}

export class OfflineGainPopup {
  private overlay: HTMLDivElement;
  private animFrame: number = 0;
  private flyFrames: number[] = [];

  constructor(data: OfflineGainData, onCollect: (gold: number) => void) {
    const overlay = document.createElement('div');
    this.overlay = overlay;

    const barPercent = Math.min(100, (data.cappedMs / (data.maxHours * 3600000)) * 100);

    overlay.style.cssText = `
      position: fixed;
      inset: 0;
      background: rgba(0,0,0,0.72);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 9999;
      animation: ofg-fade-in 0.35s ease-out;
      backdrop-filter: blur(3px);
      transition: opacity 0.3s;
    `;

    const card = document.createElement('div');
    card.style.cssText = `
      width: 380px;
      background: linear-gradient(160deg, #12141f 0%, #1a1d2e 60%, #111320 100%);
      border: 1.5px solid #f5c51840;
      border-radius: 18px;
      box-shadow:
        0 0 0 1px #0004,
        0 8px 40px #000a,
        0 0 60px #f5c51812 inset;
      padding: 28px 30px 24px;
      position: relative;
      overflow: hidden;
      animation: ofg-slide-up 0.4s cubic-bezier(0.22,1,0.36,1);
      font-family: 'Segoe UI', system-ui, sans-serif;
      transition: opacity 0.25s, transform 0.25s;
    `;

    // Decorative top glow line
    const topGlow = document.createElement('div');
    topGlow.style.cssText = `
      position: absolute;
      top: 0; left: 10%; right: 10%;
      height: 1px;
      background: linear-gradient(90deg, transparent, #f5c518aa, transparent);
    `;
    card.appendChild(topGlow);

    // Moon icon row
    const iconRow = document.createElement('div');
    iconRow.style.cssText = `display: flex; align-items: center; gap: 10px; margin-bottom: 16px;`;

    const moonIcon = document.createElement('div');
    moonIcon.textContent = '🌙';
    moonIcon.style.cssText = `font-size: 26px; line-height: 1;`;

    const titleBlock = document.createElement('div');

    const title = document.createElement('div');
    title.textContent = 'Welcome back!';
    title.style.cssText = `
      font-size: 18px; font-weight: 700; color: #f5c518;
      letter-spacing: 0.02em; text-shadow: 0 0 20px #f5c51844;
    `;

    const subtitle = document.createElement('div');
    subtitle.textContent = `Away for ${formatDuration(data.timeAwayMs)} · Wave ${data.wave}`;
    subtitle.style.cssText = `font-size: 12px; color: #8891aa; margin-top: 2px; letter-spacing: 0.03em;`;

    titleBlock.appendChild(title);
    titleBlock.appendChild(subtitle);
    iconRow.appendChild(moonIcon);
    iconRow.appendChild(titleBlock);
    card.appendChild(iconRow);

    // Divider
    const divider = document.createElement('div');
    divider.style.cssText = `height: 1px; background: linear-gradient(90deg, transparent, #ffffff12, transparent); margin-bottom: 20px;`;
    card.appendChild(divider);

    // Gold display
    const goldBlock = document.createElement('div');
    goldBlock.style.cssText = `display: flex; align-items: center; gap: 12px; margin-bottom: 20px;`;

    const coinCanvas = document.createElement('canvas');
    coinCanvas.style.cssText = `width: 56px; height: 56px; flex-shrink: 0; filter: drop-shadow(0 0 10px #ffd70088);`;
    drawGameCoin(coinCanvas, 56);

    const goldRight = document.createElement('div');

    const goldLabel = document.createElement('div');
    goldLabel.textContent = 'Gold earned offline';
    goldLabel.style.cssText = `font-size: 11px; color: #8891aa; text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 4px;`;

    const goldAmount = document.createElement('div');
    goldAmount.textContent = '+0';
    goldAmount.style.cssText = `
      font-size: 36px; font-weight: 800; color: #ffd740;
      letter-spacing: -0.01em;
      text-shadow: 0 0 30px #f5c51855, 0 2px 4px #0006;
      line-height: 1;
    `;

    goldRight.appendChild(goldLabel);
    goldRight.appendChild(goldAmount);
    goldBlock.appendChild(coinCanvas);
    goldBlock.appendChild(goldRight);
    card.appendChild(goldBlock);

    // Time bar
    const barSection = document.createElement('div');
    barSection.style.cssText = `margin-bottom: 22px;`;

    const barLabelRow = document.createElement('div');
    barLabelRow.style.cssText = `display: flex; justify-content: space-between; margin-bottom: 6px;`;

    const barLabel = document.createElement('span');
    barLabel.textContent = 'Time collected';
    barLabel.style.cssText = `font-size: 11px; color: #8891aa; text-transform: uppercase; letter-spacing: 0.07em;`;

    const barValue = document.createElement('span');
    barValue.textContent = `${formatDuration(data.cappedMs)} / ${data.maxHours}h`;
    barValue.style.cssText = `font-size: 11px; color: #c4aa60; font-weight: 600;`;

    barLabelRow.appendChild(barLabel);
    barLabelRow.appendChild(barValue);

    const barTrack = document.createElement('div');
    barTrack.style.cssText = `
      height: 8px; background: #ffffff0d; border-radius: 99px;
      overflow: hidden; border: 1px solid #ffffff08;
    `;

    const barFill = document.createElement('div');
    barFill.style.cssText = `
      height: 100%; width: 0%;
      background: linear-gradient(90deg, #c8960c, #f5c518, #ffe97a);
      border-radius: 99px;
      transition: width 1.2s cubic-bezier(0.4, 0, 0.2, 1);
      box-shadow: 0 0 8px #f5c51844;
    `;

    barTrack.appendChild(barFill);
    barSection.appendChild(barLabelRow);
    barSection.appendChild(barTrack);
    card.appendChild(barSection);

    // Upgrade hint
    if (data.maxHours < 24) {
      const hint = document.createElement('div');
      hint.textContent = `⚡ Upgrade "Night Watch" in the prestige tree to earn up to 24h offline`;
      hint.style.cssText = `font-size: 11px; color: #6b7490; text-align: center; margin-bottom: 16px; line-height: 1.5;`;
      card.appendChild(hint);
    }

    // Collect button
    const btn = document.createElement('button');
    btn.textContent = 'COLLECT';
    btn.style.cssText = `
      width: 100%; padding: 13px 0;
      background: linear-gradient(135deg, #c8960c 0%, #f5c518 50%, #e8b400 100%);
      color: #1a1200; font-size: 15px; font-weight: 800;
      letter-spacing: 0.12em; border: none; border-radius: 10px;
      cursor: pointer; position: relative; overflow: hidden;
      box-shadow: 0 4px 20px #f5c51844, 0 1px 0 #fff3 inset;
      transition: transform 0.1s, box-shadow 0.1s;
    `;

    const shimmer = document.createElement('div');
    shimmer.style.cssText = `
      position: absolute; top: 0; left: -100%; width: 60%; height: 100%;
      background: linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent);
      animation: ofg-shimmer 2s ease-in-out infinite;
      pointer-events: none;
    `;
    btn.appendChild(shimmer);

    btn.addEventListener('mouseenter', () => {
      btn.style.transform = 'translateY(-1px)';
      btn.style.boxShadow = '0 6px 28px #f5c51866, 0 1px 0 #fff3 inset';
    });
    btn.addEventListener('mouseleave', () => {
      btn.style.transform = '';
      btn.style.boxShadow = '0 4px 20px #f5c51844, 0 1px 0 #fff3 inset';
    });

    btn.addEventListener('click', () => {
      cancelAnimationFrame(this.animFrame);
      btn.disabled = true;

      // Fade out overlay entirely — coins are on document.body so they keep flying
      overlay.style.opacity = '0';
      setTimeout(() => overlay.remove(), 300);

      // Get start position: center of the coinCanvas icon
      const coinRect = coinCanvas.getBoundingClientRect();
      const startX = coinRect.left + coinRect.width / 2;
      const startY = coinRect.top + coinRect.height / 2;

      const COIN_COUNT = 8;
      const COIN_DURATION = 650;
      const STAGGER = 70;
      let landed = 0;

      for (let i = 0; i < COIN_COUNT; i++) {
        setTimeout(() => {
          this.launchCoin(
            startX + (Math.random() - 0.5) * 20,
            startY + (Math.random() - 0.5) * 20,
            data.goldTargetPos.x,
            data.goldTargetPos.y,
            COIN_DURATION,
            () => {
              landed++;
              if (landed === COIN_COUNT) {
                onCollect(data.goldGained);
                this.destroy();
              }
            }
          );
        }, i * STAGGER);
      }
    });

    card.appendChild(btn);
    overlay.appendChild(card);

    // Keyframe styles
    if (!document.getElementById('ofg-styles')) {
      const style = document.createElement('style');
      style.id = 'ofg-styles';
      style.textContent = `
        @keyframes ofg-fade-in {
          from { opacity: 0; } to { opacity: 1; }
        }
        @keyframes ofg-slide-up {
          from { opacity: 0; transform: translateY(24px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes ofg-shimmer {
          0%   { left: -100%; }
          60%  { left: 140%; }
          100% { left: 140%; }
        }
      `;
      document.head.appendChild(style);
    }

    document.body.appendChild(overlay);

    // Bar fill animation
    requestAnimationFrame(() => {
      setTimeout(() => { barFill.style.width = `${barPercent}%`; }, 120);
    });

    // Gold count-up
    this.animateCountUp(goldAmount, data.goldGained, 1500);
  }

  private launchCoin(
    startX: number, startY: number,
    endX: number, endY: number,
    duration: number,
    onDone: () => void
  ): void {
    const coin = document.createElement('canvas');
    const SIZE = 18;
    coin.style.cssText = `
      position: fixed;
      left: ${startX - SIZE / 2}px;
      top: ${startY - SIZE / 2}px;
      width: ${SIZE}px; height: ${SIZE}px;
      pointer-events: none;
      z-index: 10000;
      will-change: transform, opacity;
    `;
    drawGameCoin(coin, SIZE);
    document.body.appendChild(coin);

    // Bezier control point: arc upward and toward target
    const midX = (startX + endX) / 2 + (Math.random() - 0.5) * 80;
    const midY = Math.min(startY, endY) - 100 - Math.random() * 80;

    const startTime = performance.now();
    const animate = (now: number) => {
      const t = Math.min((now - startTime) / duration, 1);
      // Ease in-out quad
      const e = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;

      // Quadratic bezier
      const mt = 1 - e;
      const x = mt * mt * startX + 2 * mt * e * midX + e * e * endX;
      const y = mt * mt * startY + 2 * mt * e * midY + e * e * endY;

      coin.style.left = `${x - SIZE / 2}px`;
      coin.style.top = `${y - SIZE / 2}px`;
      // Scale down and fade as it approaches target
      const scale = 1 - e * 0.5;
      const opacity = t > 0.75 ? 1 - (t - 0.75) / 0.25 : 1;
      coin.style.transform = `scale(${scale})`;
      coin.style.opacity = String(opacity);

      if (t < 1) {
        const frame = requestAnimationFrame(animate);
        this.flyFrames.push(frame);
      } else {
        coin.remove();
        onDone();
      }
    };
    const frame = requestAnimationFrame(animate);
    this.flyFrames.push(frame);
  }

  private animateCountUp(el: HTMLElement, target: number, durationMs: number): void {
    const start = performance.now();
    const step = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / durationMs, 1);
      const eased = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      el.textContent = `+${formatGold(Math.floor(eased * target))}`;
      if (progress < 1) {
        this.animFrame = requestAnimationFrame(step);
      } else {
        el.textContent = `+${formatGold(target)}`;
      }
    };
    this.animFrame = requestAnimationFrame(step);
  }

  destroy(): void {
    cancelAnimationFrame(this.animFrame);
    this.flyFrames.forEach(f => cancelAnimationFrame(f));
    if (this.overlay.isConnected) this.overlay.remove();
  }
}
